import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleImage,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import { getExchangeVariantLabel } from "@babackapp/return-widget-components/utils/exchange-variant-label";
import { DiscountedPrice } from "@babackapp/return-widget-components/components/discounted-price";
import type {
  DraftLineItem,
  Order,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { InfoIcon, Trash2Icon } from "lucide-react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";

type Props = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  order: Order | null;
  currency: string;
  isRetractation?: boolean;
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
  onRemove: () => void;
};

export function SummaryLineItem({
  item,
  orderLineItems,
  order,
  currency,
  isRetractation,
  isConnectedUser = false,
  connectedUserTooltip,
  onRemove,
}: Props) {
  const t = useCustomText();
  const { title, subtitle, image, price, paidPrice, sku } = getLineItemDisplay(
    orderLineItems,
    item.lineItemId,
  );

  const exchangeLabel =
    item.resolution === "exchange" && item.exchangeVariantId
      ? getExchangeVariantLabel(order, item.lineItemId, item.exchangeVariantId)
      : null;

  const variantDisplay =
    exchangeLabel && subtitle
      ? `${subtitle} → ${exchangeLabel}`
      : (exchangeLabel ?? subtitle);

  return (
    <Article>
      <ArticleImage
        src={image ?? undefined}
        layoutId={`img-${item.lineItemId}`}
      />
      <ArticleTitle className="flex items-center gap-1.5">
        {title}
        {isRetractation && (
          <Badge variant="secondary" size="xs">
            {t("returnMethod.retractation.label")}
          </Badge>
        )}
      </ArticleTitle>
      <ArticleSubtitle>
        {[variantDisplay, `×${item.quantity}`].filter(Boolean).join(" — ")}
        {price != null && (
          <>
            {" — "}
            <DiscountedPrice
              catalogCents={price}
              paidCents={paidPrice}
              quantity={item.quantity}
              currency={currency}
            />
          </>
        )}
      </ArticleSubtitle>
      {isConnectedUser && sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{sku}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction autoHide={false}>
        <Button variant="outline" size="icon-sm" onClick={onRemove}>
          <Trash2Icon />
        </Button>
      </ArticleAction>
    </Article>
  );
}
